import { EMPTY_CTX, isHostToApp, type AppToHost, type CtxSnapshot, type ThemeTokens } from "./bridge";

type CtxListener = (ctx: CtxSnapshot) => void;
type AuthListener = (token: string | null) => void;
type ThemeListener = (theme: ThemeTokens) => void;

/**
 * Lives in the iframed SPA. Talks to the parent (tb-host-widget) via
 * window.postMessage. Sends `ready` on construction, receives `ctx.init` /
 * `ctx.update` / `auth.token` / `theme` / `resize`. Exposes accessors and
 * change subscriptions so React hooks can wrap it with `useSyncExternalStore`.
 *
 * Pinned to the host origin passed via the iframe URL `?hostOrigin=` query
 * param (TB-EMS pattern) — `*` only in dev (the local simulator).
 */
export class EmbeddedCtxSource {
  private ctx: CtxSnapshot = EMPTY_CTX;
  private authToken: string | null = null;
  private theme: ThemeTokens = {};
  private ready = false;
  private readonly ctxListeners = new Set<CtxListener>();
  private readonly authListeners = new Set<AuthListener>();
  private readonly themeListeners = new Set<ThemeListener>();
  private readonly handler = (e: MessageEvent) => this.onMessage(e);

  constructor(private readonly hostOrigin: string = "*") {
    if (typeof window === "undefined") return;
    window.addEventListener("message", this.handler);
    this.postToHost({ v: 1, type: "ready" });
  }

  destroy(): void {
    if (typeof window === "undefined") return;
    window.removeEventListener("message", this.handler);
    this.ctxListeners.clear();
    this.authListeners.clear();
    this.themeListeners.clear();
  }

  getCtx() { return this.ctx; }
  getAuthToken() { return this.authToken; }
  getTheme() { return this.theme; }
  isReady() { return this.ready; }

  onCtxChange(l: CtxListener) { this.ctxListeners.add(l); return () => this.ctxListeners.delete(l); }
  onAuthChange(l: AuthListener) { this.authListeners.add(l); return () => this.authListeners.delete(l); }
  onThemeChange(l: ThemeListener) { this.themeListeners.add(l); return () => this.themeListeners.delete(l); }

  sendAction(msg: Exclude<AppToHost, { type: "ready" }>): void { this.postToHost(msg); }

  private postToHost(msg: AppToHost): void {
    if (typeof window === "undefined") return;
    if (window.parent === window) return;
    window.parent.postMessage(msg, this.hostOrigin);
  }

  private onMessage(ev: MessageEvent): void {
    if (this.hostOrigin !== "*" && ev.origin !== this.hostOrigin) return;
    if (!isHostToApp(ev.data)) return;
    switch (ev.data.type) {
      case "ctx.init":
        this.ctx = ev.data.payload;
        this.ready = true;
        for (const l of this.ctxListeners) l(this.ctx);
        break;
      case "ctx.update":
        this.ctx = { ...this.ctx, ...ev.data.payload };
        for (const l of this.ctxListeners) l(this.ctx);
        break;
      case "auth.token":
        this.authToken = ev.data.payload.token;
        for (const l of this.authListeners) l(this.authToken);
        break;
      case "theme":
        this.theme = ev.data.payload;
        for (const l of this.themeListeners) l(this.theme);
        break;
      case "resize":
        // Iframe sizing is handled by CSS on the parent side.
        break;
    }
  }

  static isEmbedded(): boolean {
    if (typeof window === "undefined") return false;
    try { return window.parent !== window; } catch { return true; }
  }
}
